import React, { useCallback, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { FormDrawer, Badge } from '../../../components/ui';
import { useSnackBar } from '../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import { userInformationAtom } from '../../../Services/_states/auth.js';
import useProfileForm from '../hooks/useProfileForm';
import ProfileForm from './ProfileForm';
import {
  canSetStandardProfile,
  getLoggedInOrganisation,
} from '../utils/profileFormAccessUtils.js';

const CREATE_SUBTITLE =
  'Create a permission profile for an organisation. Default RBAC grants are applied automatically.';

const EDIT_SUBTITLE = 'Update profile details and session timeout settings.';

const DRAWER_WIDTH = { xs: '100%', sm: 480, md: 640, lg: 720 };

export default function ProfileFormDrawer({
  open,
  profile = null,
  onClose,
  onSuccess,
  onCreatedRedirect,
}) {
  const { showSnackBar } = useSnackBar();
  const loggedInUser = useRecoilValue(userInformationAtom);
  const showIsStandard = canSetStandardProfile(loggedInUser);
  const loggedInOrganisation = getLoggedInOrganisation(loggedInUser);
  const formState = useProfileForm({
    profile,
    onClose,
    onSuccess,
    open,
    loggedInUser,
    showIsStandard,
  });
  const {
    isEditMode,
    isSubmitting,
    isDirty,
    validationAttempt,
    submit,
    handleClose,
    form,
    errors,
    updateField,
  } = formState;

  const editBadge = useMemo(() => {
    if (!isEditMode) {
      return null;
    }
    return (
      <Badge variant="outline" size="sm" sx={{ fontWeight: 500 }}>
        {`Editing ${profile?.name || 'Profile'}`}
      </Badge>
    );
  }, [isEditMode, profile?.name]);

  const onDrawerClose = useCallback(() => {
    if (!isSubmitting) {
      handleClose();
    }
  }, [handleClose, isSubmitting]);

  const onDrawerSubmit = useCallback(async () => {
    const result = await submit();
    if (!result.success && result.message) {
      showSnackBar(result.message, 'error');
      return;
    }
    if (result.success) {
      showSnackBar(
        isEditMode ? 'Profile updated successfully.' : 'Profile created successfully.',
        'success'
      );
      if (result.redirectToPermissions && result.data?.id) {
        onCreatedRedirect?.(result.data);
      }
    }
  }, [isEditMode, onCreatedRedirect, showSnackBar, submit]);

  return (
    <FormDrawer
      open={open}
      onClose={onDrawerClose}
      title={isEditMode ? 'Edit Profile' : 'Create Profile'}
      subtitle={isEditMode ? EDIT_SUBTITLE : CREATE_SUBTITLE}
      headerBadge={editBadge}
      submitLabel={
        isSubmitting
          ? isEditMode
            ? 'Saving...'
            : 'Creating...'
          : isEditMode
            ? 'Save Changes'
            : 'Create Profile'
      }
      onSubmit={onDrawerSubmit}
      isSubmitting={isSubmitting}
      disableClose={isSubmitting}
      confirmBeforeClose={isDirty}
      width={DRAWER_WIDTH}
    >
      <ProfileForm
        form={form}
        errors={errors}
        updateField={updateField}
        validationAttempt={validationAttempt}
        showIsStandard={showIsStandard}
        organisationName={loggedInOrganisation?.name || profile?.organisation?.name || ''}
        isEditMode={isEditMode}
      />
    </FormDrawer>
  );
}
